import { useEffect, useRef, useState } from 'react';
import { TVClock } from './TVClock';
import {
  TV_NAME_MODES,
  TV_PALETTE,
  TV_SCALE_KEYS,
  type TVNameMode,
  type TVScaleKey,
} from './tv-mode.constants';
import { useTVScale } from './TVScaleContext';
import type { TVClubInfo } from '@/utils/tv-mode-data';

interface Props {
  clubInfo: TVClubInfo | null;
  onExit?: () => void;
}

export const TVHeader = ({ clubInfo, onExit }: Props) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        background: TV_PALETTE.hdrBg,
        borderBottom: `${Math.max(2, s(3))}px solid ${TV_PALETTE.accent}`,
        padding: `0 ${s(24)}px`,
        height: s(56),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: s(14) }}>
        <button
          type="button"
          onClick={onExit}
          title="Salir de Modo TV"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: onExit ? 'pointer' : 'default',
          }}
        >
          <img
            src="/assets/images/logo.svg"
            alt="GetMatch"
            height={s(36)}
            style={{ height: s(36), width: 'auto', display: 'block' }}
          />
        </button>
        <TVConfigBadge />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: s(20) }}>
        {clubInfo && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: s(14),
              borderRight: `1px solid ${TV_PALETTE.border}`,
              paddingRight: s(20),
            }}
          >
            {clubInfo.imageUrl && (
              <TVClubHeaderImage
                url={clubInfo.imageUrl}
                title={clubInfo.primaryName}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: s(2) }}>
              <div
                style={{
                  fontSize: s(10),
                  fontWeight: 800,
                  color: TV_PALETTE.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                }}
              >
                {clubInfo.multiple ? 'Sedes' : 'Sede'}
              </div>
              <div
                className="font-gilroy"
                style={{
                  fontSize: s(28),
                  fontWeight: 700,
                  color: TV_PALETTE.navyDeep,
                  letterSpacing: '0.01em',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: s(10),
                }}
              >
                <span>{clubInfo.primaryName}</span>
                {clubInfo.city && (
                  <span
                    style={{
                      fontSize: s(14),
                      fontWeight: 500,
                      color: TV_PALETTE.textMuted,
                      letterSpacing: '0.02em',
                    }}
                  >
                    · {clubInfo.city}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <TVClock />
      </div>
    </div>
  );
};

/**
 * Pill "● MODO TV – EN VIVO" que actúa también como entrada a la
 * configuración: hover muestra "CONFIGURAR", click abre el popover.
 */
function TVConfigBadge() {
  const { s } = useTVScale();
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const showTooltip = hover && !open;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Configurar Modo TV"
        style={{
          background: TV_PALETTE.accent2,
          color: '#fff',
          fontSize: s(10),
          fontWeight: 800,
          padding: `${s(4)}px ${s(12)}px`,
          borderRadius: 4,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          boxShadow: open
            ? '0 2px 6px rgba(232, 69, 46, 0.45)'
            : '0 1px 2px rgba(1, 31, 82, 0.08)',
          border: 'none',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, transform 0.15s',
          transform: open ? 'translateY(-1px)' : 'none',
        }}
      >
        ● MODO TV – EN VIVO
      </button>

      {showTooltip && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: TV_PALETTE.navyDeep,
            color: '#fff',
            fontSize: s(9),
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            padding: `${s(4)}px ${s(8)}px`,
            borderRadius: 4,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(1, 31, 82, 0.25)',
            zIndex: 50,
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 8,
              height: 8,
              background: TV_PALETTE.navyDeep,
            }}
          />
          Configurar
        </div>
      )}

      {open && <TVConfigPopover onClose={() => setOpen(false)} />}
    </div>
  );
}

function TVConfigPopover({ onClose }: { onClose: () => void }) {
  const { s } = useTVScale();
  return (
    <div
      role="dialog"
      aria-label="Configuración del Modo TV"
      style={{
        position: 'absolute',
        top: 'calc(100% + 10px)',
        left: 0,
        minWidth: s(260),
        background: TV_PALETTE.card,
        border: `1px solid ${TV_PALETTE.border}`,
        borderRadius: 8,
        boxShadow:
          '0 12px 32px rgba(1, 31, 82, 0.18), 0 2px 6px rgba(1, 31, 82, 0.08)',
        padding: `${s(14)}px ${s(16)}px`,
        zIndex: 60,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -6,
          left: s(28),
          width: 12,
          height: 12,
          background: TV_PALETTE.card,
          borderTop: `1px solid ${TV_PALETTE.border}`,
          borderLeft: `1px solid ${TV_PALETTE.border}`,
          transform: 'rotate(45deg)',
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: s(10),
        }}
      >
        <div
          style={{
            fontSize: s(9),
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: TV_PALETTE.textMuted,
          }}
        >
          Configuración
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          style={{
            background: 'transparent',
            border: 'none',
            color: TV_PALETTE.textMuted,
            fontSize: s(14),
            lineHeight: 1,
            cursor: 'pointer',
            padding: 2,
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          fontSize: s(11),
          fontWeight: 700,
          color: TV_PALETTE.navy,
          marginBottom: s(8),
        }}
      >
        Tamaño de visualización
      </div>
      <TVScaleSelector />
      <div
        style={{
          marginTop: s(8),
          fontSize: s(10),
          color: TV_PALETTE.textSubtle,
          lineHeight: 1.4,
        }}
      >
        Ajustá el tamaño según la distancia y resolución de la pantalla.
      </div>

      <div
        style={{
          height: 1,
          background: TV_PALETTE.borderSoft,
          margin: `${s(14)}px 0`,
        }}
      />

      <div
        style={{
          fontSize: s(11),
          fontWeight: 700,
          color: TV_PALETTE.navy,
          marginBottom: s(8),
        }}
      >
        Nombres de jugadores
      </div>
      <TVNameModeSelector />
      <div
        style={{
          marginTop: s(8),
          fontSize: s(10),
          color: TV_PALETTE.textSubtle,
          lineHeight: 1.4,
        }}
      >
        Elegí entre apellido + inicial o nombre y apellido completos.
      </div>
    </div>
  );
}

const TV_NAME_MODE_LABELS: Record<TVNameMode, string> = {
  abbreviated: 'Apellido + Inicial',
  full: 'Nombre completo',
};

function TVNameModeSelector() {
  const { nameMode, setNameMode, s } = useTVScale();
  return (
    <div
      role="group"
      aria-label="Formato de nombre de jugadores"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        borderRadius: 6,
        background: TV_PALETTE.cardSoft,
        border: `1px solid ${TV_PALETTE.border}`,
      }}
    >
      {TV_NAME_MODES.map((key) => {
        const active = key === nameMode;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => setNameMode(key as TVNameMode)}
            title={TV_NAME_MODE_LABELS[key]}
            style={{
              padding: `${s(5)}px ${s(12)}px`,
              borderRadius: 4,
              border: 'none',
              background: active ? TV_PALETTE.navyDeep : 'transparent',
              color: active ? '#fff' : TV_PALETTE.textMuted,
              fontWeight: 700,
              fontSize: s(11),
              letterSpacing: '0.04em',
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {TV_NAME_MODE_LABELS[key]}
          </button>
        );
      })}
    </div>
  );
}

function TVScaleSelector() {
  const { scale, setScale, s } = useTVScale();
  return (
    <div
      role="group"
      aria-label="Tamaño del Modo TV"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        borderRadius: 6,
        background: TV_PALETTE.cardSoft,
        border: `1px solid ${TV_PALETTE.border}`,
      }}
    >
      {TV_SCALE_KEYS.map((key) => {
        const active = key === scale;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => setScale(key as TVScaleKey)}
            title={`Tamaño ${key}`}
            style={{
              minWidth: s(32),
              padding: `${s(5)}px ${s(10)}px`,
              borderRadius: 4,
              border: 'none',
              background: active ? TV_PALETTE.navyDeep : 'transparent',
              color: active ? '#fff' : TV_PALETTE.textMuted,
              fontWeight: 800,
              fontSize: s(12),
              letterSpacing: '0.06em',
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {key}
          </button>
        );
      })}
    </div>
  );
}

function TVClubHeaderImage({ url, title }: { url: string; title: string }) {
  const { s } = useTVScale();
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  const size = s(46);
  return (
    <img
      src={url}
      alt=""
      role="presentation"
      width={size}
      height={size}
      onError={() => setOk(false)}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        objectFit: 'cover',
        borderRadius: 8,
        border: `1px solid ${TV_PALETTE.border}`,
        background: TV_PALETTE.cardSoft,
      }}
      title={title}
    />
  );
}
